import React, { createContext, useContext, useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    
    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch (e) {
        // Invalid stored data, clear it
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      console.log('[Auth] Attempting login for:', email);
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      console.log('[Auth] Login response status:', response.status);
      
      if (!response.ok) {
        console.error('[Auth] Login failed:', data);
        throw new Error(data.detail || 'Login failed');
      }

      console.log('[Auth] Login successful, setting user data');
      setUser(data.user);
      setToken(data.token);
      
      try {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        localStorage.setItem('userEmail', data.user.email);
        sessionStorage.setItem('admin_auth', btoa(`${email}:${password}`));
        sessionStorage.setItem('admin_username', data.user.first_name || 'Admin');
        console.log('[Auth] Session data stored successfully');
      } catch (storageError) {
        console.error('[Auth] Storage error:', storageError);
        // Continue anyway - login still works even if storage fails
      }
      
      return data;
    } catch (error) {
      console.error('[Auth] Login error:', error);
      throw error;
    }
  };

  const register = async ({ email, password, first_name, last_name }) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, first_name, last_name, role: 'viewer' })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      setUser(data.user);
      setToken(data.token);
      try {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        localStorage.setItem('userEmail', data.user.email);
        sessionStorage.setItem('admin_auth', btoa(`${email}:${password}`));
        sessionStorage.setItem('admin_username', data.user.first_name || 'Member');
      } catch (_) { /* storage errors non-fatal */ }
      return data;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('userEmail');
    sessionStorage.removeItem('admin_auth');
    sessionStorage.removeItem('admin_username');
  };

  const isAdmin = () => {
    return user?.role === 'admin' || user?.role === 'super_admin';
  };

  const isEditor = () => {
    return user?.role === 'editor' || user?.role === 'admin' || user?.role === 'super_admin';
  };

  const getAuthHeader = () => {
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      isAdmin,
      isEditor,
      getAuthHeader,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
